/**
 * Utility functions for handling authentication tokens and secure storage
 */

import { resetUnauthorizedFlag } from "@config/axios/axios.config";
import type { User } from "@features/Login/types";
import { clearUser, initializeUser } from "@store/slices/userSlice";
import { store } from "@store/store";

const USER_KEY = "user";
const TOKEN_KEY: string = "token";

export const getAuthToken = (): string | null => {
	try {
		return localStorage.getItem(TOKEN_KEY);
	} catch (error) {
		console.error("An error occured while getting token:", error);
		return null;
	}
};

export const setAuthToken = (token: string): void => {
	try {
		localStorage.setItem(TOKEN_KEY, token);
	} catch (error) {
		console.error("An error occured while setting token:", error);
	}
};

export const removeAuthToken = (): void => {
	try {
		localStorage.removeItem(TOKEN_KEY);
		store.dispatch(clearUser());
	} catch (error) {
		console.error("An error occured while removing token:", error);
	}
};

export const getUser = (): User | null => {
	try {
		const user = localStorage.getItem(USER_KEY);
		return user ? JSON.parse(user) : null;
	} catch (error) {
		console.error("An error occured while getting user:", error);
		return null;
	}
};

export const setUser = (user: User): void => {
	try {
		localStorage.setItem(USER_KEY, JSON.stringify(user));
	} catch (error) {
		console.error("An error occured while setting user:", error);
	}
};

export const removeUser = (): void => {
	try {
		localStorage.removeItem(USER_KEY);
	} catch (error) {
		console.error("An error occured while removing user:", error);
	}
};

export const clearAuthData = (): void => {
	removeAuthToken();
	removeUser();
	sessionStorage.clear();

	// Clear auth-related cookies
	document.cookie.split(";").forEach(cookie => {
		const [name] = cookie.trim().split("=");
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	});
};

export const initializeAuthState = (): void => {
	resetUnauthorizedFlag();
	const user = getUser();
	const token = getAuthToken();
	store.dispatch(initializeUser({ user, token }));
};
